import jsPDF from 'jspdf'
import { Thread, QANode, Answer } from '@/types'

interface ExportOptions {
  thread: Thread
  qaNodes: Record<string, QANode>
  entityName: string
}

interface TextSegment {
  text: string
  bold?: boolean
  italic?: boolean
  code?: boolean
}

// Parse HTML content and extract formatted text with images
async function parseHTMLContent(html: string): Promise<{ segments: TextSegment[], images: Array<{ src: string, alt: string }> }> {
  if (!html) return { segments: [], images: [] }
  
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const segments: TextSegment[] = []
    const images: Array<{ src: string, alt: string }> = []
    
    // Recursively process DOM nodes
    const processNode = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || ''
        if (text.trim()) {
          const parent = node.parentElement
          segments.push({
            text,
            bold: parent?.tagName === 'STRONG' || parent?.tagName === 'B',
            italic: parent?.tagName === 'EM' || parent?.tagName === 'I',
            code: parent?.tagName === 'CODE'
          })
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element
        
        if (element.tagName === 'IMG') {
          const src = element.getAttribute('src') || ''
          const alt = element.getAttribute('alt') || ''
          images.push({ src, alt })
        } else if (element.tagName === 'BR') {
          segments.push({ text: '\n' })
        } else if (element.tagName === 'P' || element.tagName === 'DIV') {
          // Process children first
          Array.from(element.childNodes).forEach(processNode)
          // Add line break after paragraph/div
          if (segments.length > 0 && segments[segments.length - 1].text !== '\n') {
            segments.push({ text: '\n' })
          }
        } else if (element.tagName === 'LI') {
          segments.push({ text: '• ' })
          Array.from(element.childNodes).forEach(processNode)
          segments.push({ text: '\n' })
        } else if (element.tagName === 'UL' || element.tagName === 'OL') {
          Array.from(element.childNodes).forEach(processNode)
          segments.push({ text: '\n' })
        } else {
          // Process children for other elements
          Array.from(element.childNodes).forEach(processNode)
        }
      }
    }
    
    processNode(doc.body)
    return { segments, images }
  } catch {
    return { segments: [{ text: html }], images: [] }
  }
}

// Load image from URL and convert to base64
async function loadImage(src: string): Promise<{ data: string, width: number, height: number } | null> {
  try {
    return new Promise((resolve) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        if (ctx) {
          ctx.drawImage(img, 0, 0)
          const data = canvas.toDataURL('image/jpeg', 0.8)
          resolve({ data, width: img.width, height: img.height })
        } else {
          resolve(null)
        }
      }
      
      img.onerror = () => resolve(null)
      img.src = src
    })
  } catch {
    return null
  }
}

export async function exportThreadToPDF(options: ExportOptions): Promise<void> {
  const { thread, qaNodes, entityName } = options
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  })
  
  let yPosition = 20
  const pageWidth = doc.internal.pageSize.width  // 210mm for A4
  const pageHeight = doc.internal.pageSize.height  // 297mm for A4
  const margin = 15
  const maxWidth = pageWidth - 2 * margin
  const lineHeight = 6
  const sectionSpacing = 4
  
  // Helper function to check if we need a new page and add one if needed
  const checkNewPage = (requiredSpace: number) => {
    if (yPosition + requiredSpace > pageHeight - margin) {
      doc.addPage()
      yPosition = margin
      return true
    }
    return false
  }
  
  // Helper function to add text with proper word wrap and line-by-line pagination
  const addText = (text: string, fontSize: number, fontStyle: 'normal' | 'bold' | 'italic' | 'bolditalic' = 'normal', indent: number = 0) => {
    if (!text || text.trim().length === 0) return
    
    doc.setFontSize(fontSize)
    doc.setFont('helvetica', fontStyle)
    
    // Split text into lines that fit within the available width
    const lines = doc.splitTextToSize(text, maxWidth - indent)
    
    // Add each line individually, checking for page breaks between lines
    lines.forEach((line: string) => {
      checkNewPage(lineHeight)
      doc.text(line, margin + indent, yPosition)
      yPosition += lineHeight
    })
  }
  
  // Helper to add formatted content (rich text)
  const addFormattedContent = async (html: string, indent: number = 0) => {
    const { segments, images } = await parseHTMLContent(html)
    
    // Store the current text color to preserve it across formatted segments
    const currentColor = doc.getTextColor()
    
    // Add text segments with formatting
    let currentLine = ''
    let currentX = margin + indent
    const startX = margin + indent
    
    for (const segment of segments) {
      if (segment.text === '\n') {
        // New line
        yPosition += lineHeight
        currentLine = ''
        currentX = startX
        checkNewPage(lineHeight)
        continue
      }
      
      // Determine font style
      let fontStyle: 'normal' | 'bold' | 'italic' | 'bolditalic' = 'normal'
      if (segment.bold && segment.italic) fontStyle = 'bolditalic'
      else if (segment.bold) fontStyle = 'bold'
      else if (segment.italic) fontStyle = 'italic'
      
      doc.setFont('helvetica', fontStyle)
      
      // Handle code with monospace font
      if (segment.code) {
        doc.setFont('courier', 'normal')
        doc.setFillColor(240, 240, 240)
        const textWidth = doc.getTextWidth(segment.text)
        doc.rect(currentX - 1, yPosition - 4, textWidth + 2, lineHeight, 'F')
      }
      
      // Split segment if it doesn't fit on current line
      const words = segment.text.split(' ')
      for (let i = 0; i < words.length; i++) {
        const word = words[i] + (i < words.length - 1 ? ' ' : '')
        const wordWidth = doc.getTextWidth(word)
        
        if (currentX + wordWidth > pageWidth - margin) {
          // Word doesn't fit, move to next line
          yPosition += lineHeight
          currentX = startX
          checkNewPage(lineHeight)
          currentLine = ''
        }
        
        doc.text(word, currentX, yPosition)
        currentX += wordWidth
        currentLine += word
      }
    }
    
    // Move to next line after content
    if (currentLine) {
      yPosition += lineHeight
    }
    
    // Reset text color to black for images
    const imageTextColor = doc.getTextColor()
    
    // Add images
    for (const image of images) {
      const imageData = await loadImage(image.src)
      if (imageData) {
        const maxImageWidth = maxWidth - indent
        const maxImageHeight = 80 // Max height in mm
        
        // Calculate scaled dimensions
        let imgWidth = imageData.width * 0.264583 // Convert px to mm (96 DPI)
        let imgHeight = imageData.height * 0.264583
        
        if (imgWidth > maxImageWidth) {
          const ratio = maxImageWidth / imgWidth
          imgWidth = maxImageWidth
          imgHeight = imgHeight * ratio
        }
        
        if (imgHeight > maxImageHeight) {
          const ratio = maxImageHeight / imgHeight
          imgHeight = maxImageHeight
          imgWidth = imgWidth * ratio
        }
        
        checkNewPage(imgHeight + 4)
        
        doc.addImage(imageData.data, 'JPEG', margin + indent, yPosition, imgWidth, imgHeight)
        yPosition += imgHeight + 4
      }
    }
  }
  
  // Add title section
  addText(`Entity: ${entityName}`, 16, 'bold')
  yPosition += 2
  addText(`Thread: ${thread.title}`, 14, 'bold')
  yPosition += sectionSpacing + 4
  
  // Draw a separator line
  doc.setDrawColor(200, 200, 200)
  doc.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += sectionSpacing + 2
  
  // Build Q&A tree with proper pagination
  const traverse = async (nodeId: string | undefined, level: number) => {
    if (!nodeId) return
    const node = qaNodes[nodeId]
    if (!node) return
    
    const indent = 0  // No indentation for any level
    
    if (node.type === 'question') {
      // Add question with proper formatting and rich text (no background, no label)
      if (node.text && node.text.trim()) {
        checkNewPage(lineHeight + 4)
        
        doc.setTextColor(0, 0, 0)
        await addFormattedContent(node.text, indent)
        yPosition += sectionSpacing
      }
      
      // Add answer if exists
      if (node.answerId) {
        await traverse(node.answerId, level)
      }
    } else if (node.type === 'answer') {
      const answerNode = node as Answer
      
      // Add answer with colored text based on status (no background, no label)
      if (answerNode.text && answerNode.text.trim()) {
        const answerColor: [number, number, number] = answerNode.status === 'conclusive' ? [34, 197, 94] : [239, 68, 68]
        
        checkNewPage(lineHeight + 4)
        
        // Set answer color for the content
        doc.setTextColor(answerColor[0], answerColor[1], answerColor[2])
        await addFormattedContent(answerNode.text, indent)
        yPosition += sectionSpacing
      }
      
      // Add consequences inline if any (no label)
      if (answerNode.consequences && answerNode.consequences.length > 0) {
        const answerColor: [number, number, number] = answerNode.status === 'conclusive' ? [34, 197, 94] : [239, 68, 68]
        
        checkNewPage(lineHeight * 2)
        
        let currentX = margin + indent
        
        // Add each consequence on the same line or wrap if needed
        answerNode.consequences.forEach((consequence, index) => {
          const isCompleted = Boolean(consequence.completedAt)
          const checkmark = isCompleted ? '[X]' : '[ ]'
          const consequenceText = consequence.title || ''
          
          if (consequenceText) {
            const fullText = `${checkmark} ${consequenceText}${index < answerNode.consequences.length - 1 ? ', ' : ''}`
            
            doc.setFont('helvetica', 'bold')
            if (isCompleted) {
              doc.setTextColor(100, 100, 100)
            } else {
              doc.setTextColor(answerColor[0], answerColor[1], answerColor[2])
            }
            
            const textWidth = doc.getTextWidth(fullText)
            
            // Check if text fits on current line
            if (currentX + textWidth > pageWidth - margin) {
              // Move to next line
              yPosition += lineHeight
              currentX = margin + indent
              checkNewPage(lineHeight)
            }
            
            doc.text(fullText, currentX, yPosition)
            currentX += textWidth + 2
          }
        })
        
        yPosition += lineHeight + sectionSpacing
        doc.setTextColor(0, 0, 0)
      }
      
      // Process follow-up questions (array)
      if (answerNode.followUpQuestionIds && answerNode.followUpQuestionIds.length > 0) {
        for (const followUpId of answerNode.followUpQuestionIds) {
          await traverse(followUpId, level + 1)
        }
      }
    }
  }
  
  // Start traversing from root question (now async)
  await traverse(thread.rootQuestionId, 0)
  
  // Add page numbers to all pages
  const totalPages = doc.getNumberOfPages()
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(128, 128, 128)
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth / 2,
      pageHeight - 8,
      { align: 'center' }
    )
  }
  
  // Save the PDF with sanitized filename
  const sanitize = (str: string) => str.replace(/[^a-z0-9_\-\.]/gi, '_').substring(0, 100)
  const filename = `${sanitize(entityName)}_${sanitize(thread.title)}_${new Date().getTime()}.pdf`
  doc.save(filename)
}

// Export all threads of an entity to a single PDF
interface ExportAllThreadsOptions {
  entityName: string
  threads: Thread[]
  qaNodes: Record<string, QANode>
}

export async function exportAllThreadsToPDF(options: ExportAllThreadsOptions): Promise<void> {
  const { entityName, threads, qaNodes } = options
  
  if (threads.length === 0) {
    throw new Error('No threads to export')
  }
  
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  })
  
  let yPosition = 20
  const pageWidth = doc.internal.pageSize.width  // 210mm for A4
  const pageHeight = doc.internal.pageSize.height  // 297mm for A4
  const margin = 15
  const maxWidth = pageWidth - 2 * margin
  const lineHeight = 6
  const sectionSpacing = 4
  
  // Helper function to check if we need a new page and add one if needed
  const checkNewPage = (requiredSpace: number) => {
    if (yPosition + requiredSpace > pageHeight - margin) {
      doc.addPage()
      yPosition = margin
      return true
    }
    return false
  }
  
  // Helper function to add text with proper word wrap and line-by-line pagination
  const addText = (text: string, fontSize: number, fontStyle: 'normal' | 'bold' | 'italic' | 'bolditalic' = 'normal', indent: number = 0) => {
    if (!text || text.trim().length === 0) return
    
    doc.setFontSize(fontSize)
    doc.setFont('helvetica', fontStyle)
    
    // Split text into lines that fit within the available width
    const lines = doc.splitTextToSize(text, maxWidth - indent)
    
    // Add each line individually, checking for page breaks between lines
    lines.forEach((line: string) => {
      checkNewPage(lineHeight)
      doc.text(line, margin + indent, yPosition)
      yPosition += lineHeight
    })
  }
  
  // Helper to add formatted content (rich text)
  const addFormattedContent = async (html: string, indent: number = 0) => {
    const { segments, images } = await parseHTMLContent(html)
    
    // Store the current text color to preserve it across formatted segments
    const currentColor = doc.getTextColor()
    
    // Add text segments with formatting
    let currentLine = ''
    let currentX = margin + indent
    const startX = margin + indent
    
    for (const segment of segments) {
      if (segment.text === '\n') {
        // New line
        yPosition += lineHeight
        currentLine = ''
        currentX = startX
        checkNewPage(lineHeight)
        continue
      }
      
      // Determine font style
      let fontStyle: 'normal' | 'bold' | 'italic' | 'bolditalic' = 'normal'
      if (segment.bold && segment.italic) fontStyle = 'bolditalic'
      else if (segment.bold) fontStyle = 'bold'
      else if (segment.italic) fontStyle = 'italic'
      
      doc.setFont('helvetica', fontStyle)
      
      // Handle code with monospace font
      if (segment.code) {
        doc.setFont('courier', 'normal')
        doc.setFillColor(240, 240, 240)
        const textWidth = doc.getTextWidth(segment.text)
        doc.rect(currentX - 1, yPosition - 4, textWidth + 2, lineHeight, 'F')
      }
      
      // Split segment if it doesn't fit on current line
      const words = segment.text.split(' ')
      for (let i = 0; i < words.length; i++) {
        const word = words[i] + (i < words.length - 1 ? ' ' : '')
        const wordWidth = doc.getTextWidth(word)
        
        if (currentX + wordWidth > pageWidth - margin) {
          // Word doesn't fit, move to next line
          yPosition += lineHeight
          currentX = startX
          checkNewPage(lineHeight)
          currentLine = ''
        }
        
        doc.text(word, currentX, yPosition)
        currentX += wordWidth
        currentLine += word
      }
    }
    
    // Move to next line after content
    if (currentLine) {
      yPosition += lineHeight
    }
    
    // Reset text color to black for images
    const imageTextColor = doc.getTextColor()
    
    // Add images
    for (const image of images) {
      const imageData = await loadImage(image.src)
      if (imageData) {
        const maxImageWidth = maxWidth - indent
        const maxImageHeight = 80 // Max height in mm
        
        // Calculate scaled dimensions
        let imgWidth = imageData.width * 0.264583 // Convert px to mm (96 DPI)
        let imgHeight = imageData.height * 0.264583
        
        if (imgWidth > maxImageWidth) {
          const ratio = maxImageWidth / imgWidth
          imgWidth = maxImageWidth
          imgHeight = imgHeight * ratio
        }
        
        if (imgHeight > maxImageHeight) {
          const ratio = maxImageHeight / imgHeight
          imgHeight = maxImageHeight
          imgWidth = imgWidth * ratio
        }
        
        checkNewPage(imgHeight + 4)
        
        doc.addImage(imageData.data, 'JPEG', margin + indent, yPosition, imgWidth, imgHeight)
        yPosition += imgHeight + 4
      }
    }
  }
  
  // Build Q&A tree with proper pagination
  const traverse = async (nodeId: string | undefined, level: number) => {
    if (!nodeId) return
    const node = qaNodes[nodeId]
    if (!node) return
    
    const indent = 0  // No indentation for any level
    
    if (node.type === 'question') {
      // Add question with proper formatting and rich text (no background, no label)
      if (node.text && node.text.trim()) {
        checkNewPage(lineHeight + 4)
        
        doc.setTextColor(0, 0, 0)
        await addFormattedContent(node.text, indent)
        yPosition += sectionSpacing
      }
      
      // Add answer if exists
      if (node.answerId) {
        await traverse(node.answerId, level)
      }
    } else if (node.type === 'answer') {
      const answerNode = node as Answer
      
      // Add answer with colored text based on status (no background, no label)
      if (answerNode.text && answerNode.text.trim()) {
        const answerColor: [number, number, number] = answerNode.status === 'conclusive' ? [34, 197, 94] : [239, 68, 68]
        
        checkNewPage(lineHeight + 4)
        
        // Set answer color for the content
        doc.setTextColor(answerColor[0], answerColor[1], answerColor[2])
        await addFormattedContent(answerNode.text, indent)
        yPosition += sectionSpacing
      }
      
      // Add consequences inline if any (no label)
      if (answerNode.consequences && answerNode.consequences.length > 0) {
        const answerColor: [number, number, number] = answerNode.status === 'conclusive' ? [34, 197, 94] : [239, 68, 68]
        
        checkNewPage(lineHeight * 2)
        
        let currentX = margin + indent
        
        // Add each consequence on the same line or wrap if needed
        answerNode.consequences.forEach((consequence, index) => {
          const isCompleted = Boolean(consequence.completedAt)
          const checkmark = isCompleted ? '[X]' : '[ ]'
          const consequenceText = consequence.title || ''
          
          if (consequenceText) {
            const fullText = `${checkmark} ${consequenceText}${index < answerNode.consequences.length - 1 ? ', ' : ''}`
            
            doc.setFont('helvetica', 'bold')
            if (isCompleted) {
              doc.setTextColor(100, 100, 100)
            } else {
              doc.setTextColor(answerColor[0], answerColor[1], answerColor[2])
            }
            
            const textWidth = doc.getTextWidth(fullText)
            
            // Check if text fits on current line
            if (currentX + textWidth > pageWidth - margin) {
              // Move to next line
              yPosition += lineHeight
              currentX = margin + indent
              checkNewPage(lineHeight)
            }
            
            doc.text(fullText, currentX, yPosition)
            currentX += textWidth + 2
          }
        })
        
        yPosition += lineHeight + sectionSpacing
        doc.setTextColor(0, 0, 0)
      }
      
      // Process follow-up questions (array)
      if (answerNode.followUpQuestionIds && answerNode.followUpQuestionIds.length > 0) {
        for (const followUpId of answerNode.followUpQuestionIds) {
          await traverse(followUpId, level + 1)
        }
      }
    }
  }
  
  // Add cover page with entity info
  addText(`Entity: ${entityName}`, 18, 'bold')
  yPosition += 2
  addText(`Total Threads: ${threads.length}`, 12, 'normal')
  yPosition += sectionSpacing + 4
  
  // Draw a separator line
  doc.setDrawColor(200, 200, 200)
  doc.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += sectionSpacing + 2
  
  // Process each thread on a new page
  for (let i = 0; i < threads.length; i++) {
    const thread = threads[i]
    
    // Start each thread on a new page (except the first one if we have space)
    if (i > 0) {
      doc.addPage()
      yPosition = margin
    }
    
    // Add thread title
    addText(`Thread ${i + 1}: ${thread.title}`, 14, 'bold')
    yPosition += 2
    addText(`Updated: ${new Date(thread.updatedAt).toLocaleString()}`, 9, 'normal')
    yPosition += sectionSpacing + 2
    
    // Draw a separator line
    doc.setDrawColor(200, 200, 200)
    doc.line(margin, yPosition, pageWidth - margin, yPosition)
    yPosition += sectionSpacing + 2
    
    // Add thread content
    await traverse(thread.rootQuestionId, 0)
    
    // Add some spacing before next thread
    yPosition += sectionSpacing * 2
  }
  
  // Add page numbers to all pages
  const totalPages = doc.getNumberOfPages()
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(128, 128, 128)
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth / 2,
      pageHeight - 8,
      { align: 'center' }
    )
  }
  
  // Save the PDF with sanitized filename
  const sanitize = (str: string) => str.replace(/[^a-z0-9_\-\.]/gi, '_').substring(0, 100)
  const filename = `${sanitize(entityName)}_ALL_THREADS_${new Date().getTime()}.pdf`
  doc.save(filename)
}

// Export all entities to PDFs and zip them
interface Entity {
  id: string
  name: string
  threadIds: string[]
}

interface ExportAllEntitiesOptions {
  entities: Entity[]
  threads: Record<string, Thread>
  qaNodes: Record<string, QANode>
}

export async function exportAllEntitiesToZip(options: ExportAllEntitiesOptions): Promise<void> {
  const { entities, threads, qaNodes } = options
  
  if (entities.length === 0) {
    throw new Error('No entities to export')
  }
  
  // Dynamically import JSZip
  const JSZip = (await import('jszip')).default
  const zip = new JSZip()
  
  // Process each entity
  for (const entity of entities) {
    const entityThreads = entity.threadIds
      .map(id => threads[id])
      .filter(Boolean)
    
    if (entityThreads.length === 0) {
      continue // Skip entities with no threads
    }
    
    // Generate PDF for this entity
    const pdfBlob = await generateEntityPDFBlob({
      entityName: entity.name,
      threads: entityThreads,
      qaNodes
    })
    
    // Add PDF to zip
    const sanitize = (str: string) => str.replace(/[^a-z0-9_\-\.]/gi, '_').substring(0, 100)
    const filename = `${sanitize(entity.name)}.pdf`
    zip.file(filename, pdfBlob)
  }
  
  // Generate and download the zip file
  const zipBlob = await zip.generateAsync({ type: 'blob' })
  const url = URL.createObjectURL(zipBlob)
  const anchor = document.createElement('a')
  const timestamp = new Date().toISOString().replace(/[:]/g, '-').split('.')[0]
  anchor.href = url
  anchor.download = `all_entities_${timestamp}.zip`
  anchor.click()
  URL.revokeObjectURL(url)
}

// Helper function to generate a PDF as a Blob instead of downloading it
async function generateEntityPDFBlob(options: ExportAllThreadsOptions): Promise<Blob> {
  const { entityName, threads, qaNodes } = options
  
  if (threads.length === 0) {
    throw new Error('No threads to export')
  }
  
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  })
  
  let yPosition = 20
  const pageWidth = doc.internal.pageSize.width
  const pageHeight = doc.internal.pageSize.height
  const margin = 15
  const maxWidth = pageWidth - 2 * margin
  const lineHeight = 6
  const sectionSpacing = 4
  
  // Helper function to check if we need a new page and add one if needed
  const checkNewPage = (requiredSpace: number) => {
    if (yPosition + requiredSpace > pageHeight - margin) {
      doc.addPage()
      yPosition = margin
      return true
    }
    return false
  }
  
  // Helper function to add text with proper word wrap and line-by-line pagination
  const addText = (text: string, fontSize: number, fontStyle: 'normal' | 'bold' | 'italic' | 'bolditalic' = 'normal', indent: number = 0) => {
    if (!text || text.trim().length === 0) return
    
    doc.setFontSize(fontSize)
    doc.setFont('helvetica', fontStyle)
    
    const lines = doc.splitTextToSize(text, maxWidth - indent)
    
    lines.forEach((line: string) => {
      checkNewPage(lineHeight)
      doc.text(line, margin + indent, yPosition)
      yPosition += lineHeight
    })
  }
  
  // Helper to add formatted content (rich text)
  const addFormattedContent = async (html: string, indent: number = 0) => {
    const { segments, images } = await parseHTMLContent(html)
    
    let currentLine = ''
    let currentX = margin + indent
    const startX = margin + indent
    
    for (const segment of segments) {
      if (segment.text === '\n') {
        yPosition += lineHeight
        currentLine = ''
        currentX = startX
        checkNewPage(lineHeight)
        continue
      }
      
      let fontStyle: 'normal' | 'bold' | 'italic' | 'bolditalic' = 'normal'
      if (segment.bold && segment.italic) fontStyle = 'bolditalic'
      else if (segment.bold) fontStyle = 'bold'
      else if (segment.italic) fontStyle = 'italic'
      
      doc.setFont('helvetica', fontStyle)
      
      if (segment.code) {
        doc.setFont('courier', 'normal')
        doc.setFillColor(240, 240, 240)
        const textWidth = doc.getTextWidth(segment.text)
        doc.rect(currentX - 1, yPosition - 4, textWidth + 2, lineHeight, 'F')
      }
      
      const words = segment.text.split(' ')
      for (let i = 0; i < words.length; i++) {
        const word = words[i] + (i < words.length - 1 ? ' ' : '')
        const wordWidth = doc.getTextWidth(word)
        
        if (currentX + wordWidth > pageWidth - margin) {
          yPosition += lineHeight
          currentX = startX
          checkNewPage(lineHeight)
          currentLine = ''
        }
        
        doc.text(word, currentX, yPosition)
        currentX += wordWidth
        currentLine += word
      }
    }
    
    if (currentLine) {
      yPosition += lineHeight
    }
    
    for (const image of images) {
      const imageData = await loadImage(image.src)
      if (imageData) {
        const maxImageWidth = maxWidth - indent
        const maxImageHeight = 80
        
        let imgWidth = imageData.width * 0.264583
        let imgHeight = imageData.height * 0.264583
        
        if (imgWidth > maxImageWidth) {
          const ratio = maxImageWidth / imgWidth
          imgWidth = maxImageWidth
          imgHeight = imgHeight * ratio
        }
        
        if (imgHeight > maxImageHeight) {
          const ratio = maxImageHeight / imgHeight
          imgHeight = maxImageHeight
          imgWidth = imgWidth * ratio
        }
        
        checkNewPage(imgHeight + 4)
        
        doc.addImage(imageData.data, 'JPEG', margin + indent, yPosition, imgWidth, imgHeight)
        yPosition += imgHeight + 4
      }
    }
  }
  
  // Build Q&A tree with proper pagination
  const traverse = async (nodeId: string | undefined, level: number) => {
    if (!nodeId) return
    const node = qaNodes[nodeId]
    if (!node) return
    
    const indent = 0
    
    if (node.type === 'question') {
      if (node.text && node.text.trim()) {
        checkNewPage(lineHeight + 4)
        
        doc.setTextColor(0, 0, 0)
        await addFormattedContent(node.text, indent)
        yPosition += sectionSpacing
      }
      
      if (node.answerId) {
        await traverse(node.answerId, level)
      }
    } else if (node.type === 'answer') {
      const answerNode = node as Answer
      
      if (answerNode.text && answerNode.text.trim()) {
        const answerColor: [number, number, number] = answerNode.status === 'conclusive' ? [34, 197, 94] : [239, 68, 68]
        
        checkNewPage(lineHeight + 4)
        
        doc.setTextColor(answerColor[0], answerColor[1], answerColor[2])
        await addFormattedContent(answerNode.text, indent)
        yPosition += sectionSpacing
      }
      
      if (answerNode.consequences && answerNode.consequences.length > 0) {
        const answerColor: [number, number, number] = answerNode.status === 'conclusive' ? [34, 197, 94] : [239, 68, 68]
        
        checkNewPage(lineHeight * 2)
        
        let currentX = margin + indent
        
        answerNode.consequences.forEach((consequence, index) => {
          const isCompleted = Boolean(consequence.completedAt)
          const checkmark = isCompleted ? '[X]' : '[ ]'
          const consequenceText = consequence.title || ''
          
          if (consequenceText) {
            const fullText = `${checkmark} ${consequenceText}${index < answerNode.consequences.length - 1 ? ', ' : ''}`
            
            doc.setFont('helvetica', 'bold')
            if (isCompleted) {
              doc.setTextColor(100, 100, 100)
            } else {
              doc.setTextColor(answerColor[0], answerColor[1], answerColor[2])
            }
            
            const textWidth = doc.getTextWidth(fullText)
            
            if (currentX + textWidth > pageWidth - margin) {
              yPosition += lineHeight
              currentX = margin + indent
              checkNewPage(lineHeight)
            }
            
            doc.text(fullText, currentX, yPosition)
            currentX += textWidth + 2
          }
        })
        
        yPosition += lineHeight + sectionSpacing
        doc.setTextColor(0, 0, 0)
      }
      
      if (answerNode.followUpQuestionIds && answerNode.followUpQuestionIds.length > 0) {
        for (const followUpId of answerNode.followUpQuestionIds) {
          await traverse(followUpId, level + 1)
        }
      }
    }
  }
  
  // Add cover page with entity info
  addText(`Entity: ${entityName}`, 18, 'bold')
  yPosition += 2
  addText(`Total Threads: ${threads.length}`, 12, 'normal')
  yPosition += sectionSpacing + 4
  
  doc.setDrawColor(200, 200, 200)
  doc.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += sectionSpacing + 2
  
  // Process each thread on a new page
  for (let i = 0; i < threads.length; i++) {
    const thread = threads[i]
    
    if (i > 0) {
      doc.addPage()
      yPosition = margin
    }
    
    addText(`Thread ${i + 1}: ${thread.title}`, 14, 'bold')
    yPosition += 2
    addText(`Updated: ${new Date(thread.updatedAt).toLocaleString()}`, 9, 'normal')
    yPosition += sectionSpacing + 2
    
    doc.setDrawColor(200, 200, 200)
    doc.line(margin, yPosition, pageWidth - margin, yPosition)
    yPosition += sectionSpacing + 2
    
    await traverse(thread.rootQuestionId, 0)
    
    yPosition += sectionSpacing * 2
  }
  
  // Add page numbers to all pages
  const totalPages = doc.getNumberOfPages()
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(128, 128, 128)
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth / 2,
      pageHeight - 8,
      { align: 'center' }
    )
  }
  
  // Return the PDF as a Blob
  return doc.output('blob')
}
